import React, { useState, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { getEffectivePermissions } from "../config/permissions";

/**
 * ProtectedRoute - Guards routes that require authentication
 *
 * Improvements for stable back-button navigation:
 * 1. Debounces auth state changes to prevent flicker-based redirects
 * 2. Stores last known auth state to handle transient null states
 * 3. Only redirects after auth state has stabilized
 */
function ProtectedRoute({ children, userType, requiredPermission }) {
  const { t } = useTranslation();
  const { user, loading, initialLoadComplete } = useAuth();
  const location = useLocation();

  // Track stable auth state (not transient flickers)
  const [stableUser, setStableUser] = useState(user);
  const [isStabilized, setIsStabilized] = useState(false);
  const stabilizeTimerRef = useRef(null);
  const lastKnownUserRef = useRef(user);

  // Update last known user when we have a valid user
  useEffect(() => {
    if (user) {
      lastKnownUserRef.current = user;
    }
  }, [user]);

  // Debounce auth state changes to handle transient null states
  useEffect(() => {
    // Clear any pending timer
    if (stabilizeTimerRef.current) {
      clearTimeout(stabilizeTimerRef.current);
    }

    // Wait for both loading to finish AND initial load to complete
    if (loading || !initialLoadComplete) {
      // While loading, keep the last known user to prevent flicker
      setIsStabilized(false);
      return;
    }

    // Fast path: if we have a valid user, stabilize immediately (no delay needed).
    // The 50ms delay is only needed when user is null to protect against transient
    // null states during back-navigation. With a valid user there's nothing to debounce.
    if (user) {
      setStableUser(user);
      setIsStabilized(true);
      return;
    }

    // User is null — wait briefly to let auth state settle before redirecting.
    // This handles cases where auth might briefly be null during back navigation.
    stabilizeTimerRef.current = setTimeout(() => {
      setStableUser(user);
      setIsStabilized(true);
    }, 50);

    return () => {
      if (stabilizeTimerRef.current) {
        clearTimeout(stabilizeTimerRef.current);
      }
    };
  }, [user, loading, initialLoadComplete]);

  // Show loading state while auth is checking or stabilizing
  if (loading || !isStabilized) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f5f5",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #f3f3f3",
              borderTop: "3px solid #3B82F6",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <div style={{ fontSize: "16px", color: "#666" }}>{t("common.loading", "Loading...")}</div>
        </div>
        <style>
          {`
          `}
        </style>
      </div>
    );
  }

  // User not authenticated after stabilization
  if (!stableUser) {
    // Store the intended destination for redirect after login
    const redirectPath = location.pathname + location.search;

    // Redirect based on required user type
    if (userType === "admin") {
      return <Navigate to="/admin/login" replace state={{ from: redirectPath }} />;
    } else if (userType === "garage") {
      return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
    } else {
      return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
    }
  }

  // User type mismatch
  if (userType && stableUser.type !== userType) {
    if (userType === "admin" && stableUser.type !== "admin") {
      return <Navigate to="/admin/login" replace />;
    }

    if (userType === "garage" && stableUser.type !== "garage") {
      return <Navigate to="/login" replace />;
    }

    // Default: redirect to appropriate dashboard
    if (stableUser.type === "client") {
      return <Navigate to="/client-dashboard" replace />;
    } else if (stableUser.type === "garage") {
      return <Navigate to="/garage-dashboard" replace />;
    } else if (stableUser.type === "admin") {
      return <Navigate to="/admin/dashboard" replace />;
    }

    return <Navigate to="/" replace />;
  }

  // Role-based permission check for garage employees
  if (requiredPermission && stableUser.type === "garage") {
    const role = stableUser.garageRole || "owner";
    const permissions = getEffectivePermissions(role, stableUser.customPermissions || null);
    if (!permissions[requiredPermission]) {
      return <Navigate to="/garage-dashboard" replace />;
    }
  }

  return children;
}

export default ProtectedRoute;
